import type { ReactNode } from "react";
import { Link } from "react-router";
import { Avatar, type PersonInfo } from "./Avatar";

export interface PersonResultRowProps {
  person: PersonInfo & { id?: string | null };
  title: string;
  sub?: string | null;
  // Mehrzeiliger Zusatztext unter sub (z.B. ein KI-Begruendungstext) - anders als sub (einzeilig,
  // truncate) wird description NICHT abgeschnitten. Faellt weg, wenn nicht gesetzt.
  description?: ReactNode;
  actions?: ReactNode;
  // Override the default destination. Defaults to `/p/${person.id}` when an id
  // is present. Pass `null` to disable navigation entirely (static row).
  to?: string | null;
  embedded?: boolean;
}

export function PersonResultRow({
  person,
  title,
  sub,
  description,
  actions,
  to,
  embedded = false,
}: PersonResultRowProps) {
  const href = to === undefined ? (person.id ? `/p/${person.id}` : null) : to;
  // items-start statt items-center, sobald description da ist - sonst zentriert sich der Avatar
  // ueber die ganze Hoehe eines mehrzeiligen Textblocks statt an der Titelzeile auszurichten.
  const baseClass = `flex gap-3 p-3 ${description ? "items-start" : "items-center"} ${embedded ? "bg-page box-flat" : "bg-surface box"}`;
  const titleClass = `truncate text-[14px] text-ink/80${href ? " transition group-hover:text-accent" : ""}`;

  const inner = (
    <>
      <Avatar person={person} size="lg" />
      <div className="min-w-0 flex-1">
        <div className={titleClass}>{title || "–"}</div>
        {sub ? <div className="truncate text-[12px] text-ink/45">{sub}</div> : null}
        {description ? <div className="mt-1 text-[11px] leading-snug text-ink/50">{description}</div> : null}
      </div>
      {actions ? (
        <div
          className="shrink-0"
          onClick={(e) => {
            // stopPropagation alone leaves the wrapping anchor's default
            // navigation in play — preventDefault is what actually cancels it.
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {actions}
        </div>
      ) : null}
    </>
  );

  if (href) {
    return (
      <Link
        to={href}
        className={`${baseClass} group cursor-pointer transition hover:ring-accent/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30`}
      >
        {inner}
      </Link>
    );
  }
  return <div className={baseClass}>{inner}</div>;
}
